import {
    ChevronsLeft,
    ChevronDown,
    GripVertical,
    Moon,
    Pin,
    PinOff,
    Power,
    Search,
    Settings,
    Sparkles,
    Star,
    Sun,
    Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../utils/cn';
import { LiveClock } from './LiveClock';
import { motion, AnimatePresence, Variants, Reorder } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';
import { NavigationCategory, NavigationItem, isNavItemActive } from '../config/navigation';
import type { Identity } from '../types/auth';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSearchClick: () => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  galaxyEnabled: boolean;
  onToggleGalaxy: () => void;
  /** Team-scoped staff (see getTeamScopedStaff): no player search or favourites, a direct Settings link. */
  teamScoped?: boolean;
  navigationCategories: NavigationCategory[];
  favoriteItems: NavigationItem[];
  isFavorite: (itemId: string) => boolean;
  onToggleFavorite: (itemId: string) => void;
  onReorderFavorites: (itemIds: string[]) => void;
}

const SIDEBAR_OPEN_WIDTH = '17.5rem';
const SIDEBAR_CLOSED_WIDTH = '4.5rem';

const sidebarVariants: Variants = {
    open: { width: SIDEBAR_OPEN_WIDTH, minWidth: SIDEBAR_OPEN_WIDTH, transition: { type: "spring", stiffness: 420, damping: 34 } },
    closed: { width: SIDEBAR_CLOSED_WIDTH, minWidth: SIDEBAR_CLOSED_WIDTH, transition: { type: "spring", stiffness: 420, damping: 34 } }
};

const mobileSidebarVariants: Variants = {
    open: {
        x: 0,
        transition: {
            type: "tween",
            duration: 0.3,
            ease: "easeOut"
        }
    },
    closed: {
        x: "-100%",
        transition: {
            type: "tween",
            duration: 0.25,
            ease: "easeIn"
        }
    }
}

const navContainerVariants: Variants = {
    open: { transition: { staggerChildren: 0.03, delayChildren: 0.05 } },
    closed: { transition: { staggerChildren: 0.03, staggerDirection: -1 } }
};

const navItemVariants: Variants = {
    open: { y: 0, opacity: 1 },
    closed: { y: 6, opacity: 0, transition: { duration: 0.1 } }
};

const CATEGORIES_STORAGE_KEY = 'sidebar-categories';
const LOGO_URL = 'https://cdn.playcdu.co/Images/Branding/Blue/BH_NU_Asset_6.png';

function SidebarAppearanceToggles({
    theme,
    onToggleTheme,
    galaxyEnabled,
    onToggleGalaxy,
}: {
    theme: 'dark' | 'light';
    onToggleTheme: () => void;
    galaxyEnabled: boolean;
    onToggleGalaxy: () => void;
}) {
    return (
        <div className="segmented" role="group" aria-label="Appearance">
            <button
                type="button"
                onClick={onToggleGalaxy}
                className="segmented-item segmented-item-sm !px-2"
                data-active={galaxyEnabled ? 'true' : 'false'}
                title={galaxyEnabled ? "Disable Galaxy Background" : "Enable Galaxy Background"}
                aria-pressed={galaxyEnabled}
                aria-label={galaxyEnabled ? "Disable Galaxy Background" : "Enable Galaxy Background"}
            >
                <Sparkles className={cn("w-3.5 h-3.5", galaxyEnabled && "text-accent-300")} />
            </button>
            <button
                type="button"
                onClick={onToggleTheme}
                className="segmented-item segmented-item-sm !px-2"
                title={theme === 'light' ? "Switch to dark theme" : "Switch to light theme"}
                aria-label={theme === 'light' ? "Switch to dark theme" : "Switch to light theme"}
            >
                <Sun className={cn("w-3.5 h-3.5", theme === 'light' && "hidden")} />
                <Moon className={cn("w-3.5 h-3.5", theme === 'dark' && "hidden")} />
            </button>
        </div>
    );
}

function IdentityChip({ identity, compact = false }: { identity: Identity; compact?: boolean }) {
    const uuid = identity.raw?.custom?.minecraft_uuid;
    const displayName = identity.raw?.custom?.username || identity.name || identity.email;
    return (
        <div className={cn("flex items-center gap-2.5 min-w-0", compact && "justify-center")} title={identity.email}>
            {uuid ? (
                <img
                    src={`/cdu-heads/head/${uuid}/32.png`}
                    alt=""
                    className="w-8 h-8 rounded-md flex-shrink-0 ring-1 ring-glass-200/20"
                />
            ) : (
                <div className="w-8 h-8 rounded-md bg-accent-500/15 border border-accent-500/25 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-accent-300" />
                </div>
            )}
            {!compact && (
                <div className="min-w-0">
                    <p className="text-[13px] font-medium text-white truncate leading-tight">{displayName}</p>
                    <p className="text-[11px] text-glass-400 truncate leading-tight mt-0.5">{identity.email}</p>
                </div>
            )}
        </div>
    );
}

export const Sidebar = ({
    isOpen,
    setIsOpen,
    onSearchClick,
    theme,
    onToggleTheme,
    galaxyEnabled,
    onToggleGalaxy,
    teamScoped = false,
    navigationCategories,
    favoriteItems,
    isFavorite,
    onToggleFavorite,
    onReorderFavorites,
}: SidebarProps) => {
    const { identity, logout } = useAuth();
    const isMobile = useIsMobile();
    const [isPinned, setIsPinned] = useState(JSON.parse(localStorage.getItem('sidebar-pinned') || 'false'));
    const [isAnimating, setIsAnimating] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [categoryOpen, setCategoryOpen] = useState<Record<string, boolean>>(() => {
        const stored = localStorage.getItem(CATEGORIES_STORAGE_KEY);
        if (stored) {
            try {
                return JSON.parse(stored) as Record<string, boolean>;
            } catch {
                return {};
            }
        }
        return {};
    });

    // Debounced close function to prevent rapid state changes
    const debouncedClose = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsAnimating(true);
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
            setIsAnimating(false);
        }, 100);
    }, [setIsOpen]);

    // Handle mobile navigation item clicks
    const handleMobileNavClick = useCallback(() => {
        if (isMobile && isOpen) {
            debouncedClose();
        }
    }, [isMobile, isOpen, debouncedClose]);

    useEffect(() => {
        localStorage.setItem('sidebar-pinned', JSON.stringify(isPinned));
        if (isPinned) {
            setIsOpen(true);
        }
    }, [isPinned, setIsOpen]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const categoryLabels = useMemo(
        () => ['Favourites', ...navigationCategories.map((category) => category.label)],
        [navigationCategories]
    );

    useEffect(() => {
        setCategoryOpen((prev) => {
            const next = { ...prev };
            let changed = false;
            categoryLabels.forEach((label) => {
                if (next[label] === undefined) {
                    next[label] = true;
                    changed = true;
                }
            });
            if (!changed) {
                return prev;
            }
            localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }, [categoryLabels]);

    const toggleCategory = useCallback((label: string) => {
        setCategoryOpen(prev => {
            const next = { ...prev, [label]: !prev[label] };
            localStorage.setItem(CATEGORIES_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    const flatNavItems = useMemo(
        () => navigationCategories.flatMap((category) => category.items),
        [navigationCategories]
    );

    // The navigation is already filtered to what this user may open (config/navigation.ts).
    const collapsedNavItems = flatNavItems;

    const bottomNavItems = [
        ...(teamScoped
            ? []
            : [{ id: "search", label: "Search", icon: Search, onClick: onSearchClick, shortcut: '⌘K' }]),
        { id: "logout", label: "Logout", icon: Power, onClick: logout },
    ];

    const renderNavigation = (open: boolean) => (
        <>
            {!teamScoped && (
            <FavoriteCategory
                label="Favourites"
                items={favoriteItems}
                isOpen={open}
                isExpanded={categoryOpen.Favourites ?? true}
                onToggle={() => toggleCategory('Favourites')}
                onMobileClick={handleMobileNavClick}
                onReorderFavorites={onReorderFavorites}
                onToggleFavorite={onToggleFavorite}
            />
            )}
            {teamScoped && (
                <NavItem
                    to="/settings"
                    label="Settings"
                    icon={Settings}
                    isOpen={open}
                    onMobileClick={handleMobileNavClick}
                />
            )}
            {navigationCategories.map((cat) => (
                <NavCategory
                    key={cat.label}
                    label={cat.label}
                    items={cat.items}
                    isOpen={open}
                    isExpanded={categoryOpen[cat.label] ?? true}
                    onToggle={() => toggleCategory(cat.label)}
                    onMobileClick={handleMobileNavClick}
                    isFavorite={isFavorite}
                    onToggleFavorite={onToggleFavorite}
                />
            ))}
        </>
    );

    const statusRow = (
        <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
                <span className="relative flex h-2 w-2 flex-shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-pulse-slow" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
                </span>
                <span className="text-[11px] text-glass-400 truncate">System Status: OK</span>
            </div>
            <SidebarAppearanceToggles
                theme={theme}
                onToggleTheme={onToggleTheme}
                galaxyEnabled={galaxyEnabled}
                onToggleGalaxy={onToggleGalaxy}
            />
        </div>
    );

    if (isMobile) {
        return (
            <AnimatePresence mode="wait">
                {isOpen && (
                    <>
                        <motion.div
                            className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[45] mobile-sidebar-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => !isAnimating && debouncedClose()}
                        />
                        <motion.aside
                            variants={mobileSidebarVariants}
                            animate="open"
                            initial="closed"
                            exit="closed"
                            drag="x"
                            dragConstraints={{ left: -256, right: 0 }}
                            dragElastic={0.1}
                            onDragEnd={(_e, info) => {
                                // Close if dragged past 30% threshold or with enough velocity
                                if (info.offset.x < -80 || info.velocity.x < -300) {
                                    debouncedClose();
                                }
                            }}
                            className="bg-[var(--surface-drawer)] backdrop-blur-2xl border-r border-glass-200/15 fixed top-0 left-0 h-full z-50 flex flex-col w-72 mobile-sidebar shadow-popover"
                            onAnimationComplete={() => setIsAnimating(false)}
                        >
                           {/* Mobile sidebar content is the same, just wrapped differently */}
                           <div className="flex flex-col h-full overflow-hidden">
                            {/* Header */}
                            <div className="border-b border-glass-200/10 flex-shrink-0 mobile-header">
                                {/* Swipe handle indicator */}
                                <div className="flex justify-center pt-2">
                                    <div className="w-8 h-1 rounded-full bg-glass-300/30" />
                                </div>
                                <div className="flex items-center justify-between px-4 py-3">
                                    <div className="flex items-center gap-2.5">
                                        <img src={LOGO_URL} alt="Argus Logo" className="w-8 h-8 flex-shrink-0 object-contain" />
                                        <span className="text-base font-semibold tracking-tight text-purple-400">ARGUS</span>
                                    </div>
                                    <button
                                        onClick={() => !isAnimating && debouncedClose()}
                                        className="btn-icon touch-manipulation"
                                        disabled={isAnimating}
                                        title="Close menu"
                                        aria-label="Close menu"
                                    >
                                        <ChevronsLeft className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Navigation */}
                            <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
                                {renderNavigation(true)}
                            </nav>

                            {/* Footer */}
                            <div className="p-3 border-t border-glass-200/10 space-y-3">
                                {identity && <IdentityChip identity={identity} />}
                                <div className="space-y-0.5">
                                    {bottomNavItems.map(item => <NavItem key={item.id} {...item} isOpen={true} onMobileClick={handleMobileNavClick} />)}
                                </div>
                                {statusRow}
                            </div>
                        </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        )
    }

    return (
        <motion.aside
            variants={sidebarVariants}
            animate={isOpen ? "open" : "closed"}
            initial={false}
            className="bg-[var(--surface-sidebar)] backdrop-blur-2xl border-r border-glass-200/10 h-full flex-shrink-0 flex flex-col"
            onMouseEnter={() => !isPinned && setIsOpen(true)}
            onMouseLeave={() => !isPinned && setIsOpen(false)}
        >
            <div className="flex flex-col h-full overflow-hidden">
                {/* Header */}
                <div className={cn("flex items-center h-16 border-b border-glass-200/10 flex-shrink-0", isOpen ? "justify-between px-4" : "justify-center px-2")}>
                    <div className="flex items-center gap-2.5 min-w-0">
                        <img src={LOGO_URL} alt="Argus Logo" className="w-8 h-8 flex-shrink-0 object-contain" />
                        <AnimatePresence>
                        {isOpen && (
                            <motion.span
                                className="text-base font-semibold tracking-tight text-purple-400 origin-left"
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0, transition: { duration: 0.15, delay: 0.05 } }}
                                exit={{ opacity: 0, x: -8, transition: { duration: 0.1 } }}
                            >
                                ARGUS
                            </motion.span>
                        )}
                        </AnimatePresence>
                    </div>
                    <AnimatePresence>
                        {isOpen && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { delay: 0.15 } }}
                                exit={{ opacity: 0 }}
                                className="flex items-center gap-0.5"
                            >
                                <button
                                    onClick={() => setIsPinned(!isPinned)}
                                    className={cn("btn-icon btn-icon-sm", isPinned && "text-accent-300")}
                                    title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
                                    aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
                                >
                                    {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                </button>
                                {!isPinned && (
                                    <motion.button
                                        onClick={() => setIsOpen(false)}
                                        className="btn-icon btn-icon-sm"
                                        title="Collapse sidebar"
                                        aria-label="Collapse sidebar"
                                    >
                                        <ChevronsLeft className="w-4 h-4" />
                                    </motion.button>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Navigation */}
                <motion.nav
                    className={cn("flex-1 py-3 overflow-y-auto", isOpen ? "px-3 space-y-1" : "px-3 space-y-1 scrollbar-hide")}
                    variants={navContainerVariants}
                    initial={false}
                    animate="open"
                >
                    {isOpen ? (
                        renderNavigation(true)
                    ) : (
                        collapsedNavItems.map((item) => {
                            return (
                                <NavItem
                                    key={item.id}
                                    item={item}
                                    isOpen={false}
                                    onMobileClick={handleMobileNavClick}
                                />
                            );
                        })
                    )}
                </motion.nav>

                {/* Footer */}
                <motion.div className={cn("border-t border-glass-200/10", isOpen ? "p-3 space-y-3" : "p-3 space-y-2")}>
                    {identity && (
                        <IdentityChip identity={identity} compact={!isOpen} />
                    )}
                    <motion.div
                        className="space-y-0.5"
                        variants={navContainerVariants}
                        initial={false}
                        animate="open"
                    >
                        {bottomNavItems.map(item => <NavItem key={item.id} {...item} isOpen={isOpen} onMobileClick={handleMobileNavClick} />)}
                    </motion.div>

                    <AnimatePresence>
                    {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto', transition: { delay: 0.1 } }}
                        exit={{ opacity: 0, height: 0, transition: { duration: 0.1 } }}
                        className="overflow-hidden"
                    >
                        {statusRow}
                    </motion.div>
                    )}
                    </AnimatePresence>

                    {!isOpen && (
                        <div className="w-full flex justify-center pt-1">
                            <LiveClock />
                        </div>
                    )}
                </motion.div>
            </div>
        </motion.aside>
    );
};

interface NavCategoryProps {
    label: string;
    items: NavigationItem[];
    isOpen: boolean;
    isExpanded: boolean;
    onToggle: () => void;
    onMobileClick?: () => void;
    isFavorite: (itemId: string) => boolean;
    onToggleFavorite: (itemId: string) => void;
}

const CategoryToggle = ({ label, isOpen, isExpanded, onToggle }: { label: string; isOpen: boolean; isExpanded: boolean; onToggle: () => void }) => (
    <button
        type="button"
        onClick={onToggle}
        className={cn(
            "w-full flex items-center gap-2 px-3 py-1.5 mt-2 rounded-md text-left group/cat",
            "text-glass-400 hover:text-white",
            isOpen && "justify-between"
        )}
        aria-expanded={isExpanded}
    >
        <span className="eyebrow truncate min-w-0 flex-1 group-hover/cat:text-[var(--text-primary)]">{label}</span>
        {isOpen && (
            <ChevronDown className={cn("w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150", !isExpanded && "-rotate-90")} />
        )}
    </button>
);

const NavCategory = ({
    label,
    items,
    isOpen,
    isExpanded,
    onToggle,
    onMobileClick,
    isFavorite,
    onToggleFavorite,
}: NavCategoryProps) => {
    if (items.length === 0) return null;

    return (
        <motion.div variants={navItemVariants} className="space-y-0.5">
            <CategoryToggle label={label} isOpen={isOpen} isExpanded={isExpanded} onToggle={onToggle} />
            {isExpanded && (
                <div className="space-y-0.5">
                    {items.map((item) => (
                        <NavItem
                            key={item.id}
                            item={item}
                            isOpen={isOpen}
                            onMobileClick={onMobileClick}
                            showFavoriteToggle={isOpen}
                            isFavorite={isFavorite(item.id)}
                            onToggleFavorite={() => onToggleFavorite(item.id)}
                        />
                    ))}
                </div>
            )}
        </motion.div>
    );
};

interface FavoriteCategoryProps {
    label: string;
    items: NavigationItem[];
    isOpen: boolean;
    isExpanded: boolean;
    onToggle: () => void;
    onMobileClick?: () => void;
    onReorderFavorites: (itemIds: string[]) => void;
    onToggleFavorite: (itemId: string) => void;
}

const FavoriteCategory = ({
    label,
    items,
    isOpen,
    isExpanded,
    onToggle,
    onMobileClick,
    onReorderFavorites,
    onToggleFavorite,
}: FavoriteCategoryProps) => {
    return (
        <motion.div variants={navItemVariants} className="space-y-0.5">
            <CategoryToggle label={label} isOpen={isOpen} isExpanded={isExpanded} onToggle={onToggle} />
            <div
                className={cn(
                    "overflow-hidden transition-all duration-200",
                    isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                )}
                aria-hidden={!isExpanded}
            >
                <div className="space-y-0.5">
                    {items.length === 0 ? (
                        <p className="text-xs text-glass-500 px-3 py-2">
                            Star tabs to add favourites.
                        </p>
                    ) : (
                        <Reorder.Group
                            axis="y"
                            values={items}
                            onReorder={(next) => onReorderFavorites(next.map((item) => item.id))}
                            className="space-y-0.5"
                        >
                            {items.map((item) => (
                                <Reorder.Item key={item.id} value={item} className="relative">
                                    <NavItem
                                        item={item}
                                        isOpen={isOpen}
                                        onMobileClick={onMobileClick}
                                        showFavoriteToggle={isOpen}
                                        isFavorite
                                        onToggleFavorite={() => onToggleFavorite(item.id)}
                                        showDragHandle
                                    />
                                </Reorder.Item>
                            ))}
                        </Reorder.Group>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

interface NavItemProps {
    item?: NavigationItem;
    to?: string;
    label?: string;
    icon?: React.ComponentType<{ className?: string }>;
    live?: boolean;
    onClick?: () => void;
    isOpen: boolean;
    onMobileClick?: () => void;
    showFavoriteToggle?: boolean;
    isFavorite?: boolean;
    onToggleFavorite?: () => void;
    showDragHandle?: boolean;
    shortcut?: string;
}

const NavItem = ({
    item,
    to,
    label,
    icon,
    live,
    onClick,
    isOpen,
    onMobileClick,
    showFavoriteToggle = false,
    isFavorite: itemIsFavorite = false,
    onToggleFavorite,
    showDragHandle = false,
    shortcut,
}: NavItemProps) => {
    const location = useLocation();
    const isMobile = useIsMobile();
    const targetPath = item?.path ?? to;
    const itemLabel = item?.label ?? label ?? '';
    const Icon = item?.icon ?? icon ?? Users;
    const isActive = item
        ? isNavItemActive(item, location.pathname)
        : (targetPath ? location.pathname.startsWith(targetPath) : false);

    const handleClick = useCallback(() => {
        if (onClick) {
            onClick();
        }
        if (isMobile && onMobileClick) {
            onMobileClick();
        }
    }, [onClick, isMobile, onMobileClick]);

    const content = (
      <>
        <Icon className="w-[18px] h-[18px] flex-shrink-0" />
        <AnimatePresence>
          {isOpen && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0, transition: { delay: 0.05, duration: 0.15 } }}
              exit={{ opacity: 0, x: -6, transition: { duration: 0.1 } }}
              className="ml-3 whitespace-nowrap flex-1 truncate"
            >
              {itemLabel}
            </motion.span>
          )}
        </AnimatePresence>
        {isOpen && shortcut && !isMobile && (
          <span className="kbd ml-2 !h-[18px] !min-w-0 !px-1 text-[10px] opacity-70">{shortcut}</span>
        )}
        {live && (
          <motion.div
            className="w-1.5 h-1.5 bg-red-500 rounded-full ml-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: isOpen ? 1 : 0, transition: { delay: 0.15 } }}
            exit={{ opacity: 0 }}
          />
        )}
      </>
    );

    const itemClasses = cn(
        "relative flex items-center rounded-lg text-[13px] font-medium sidebar-item touch-manipulation select-none text-left",
        "transition-colors duration-150",
        isOpen ? "w-full min-h-[38px] px-3" : "w-10 h-10 justify-center mx-auto",
        isActive
            ? "bg-accent-500/15 text-accent-300"
            : "text-[var(--text-secondary)] hover:bg-glass-200/10 hover:text-white",
        isActive && isOpen && "before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full before:bg-accent-400",
        showFavoriteToggle && isOpen ? "flex-1" : "",
    );

    const title = !isOpen ? itemLabel : undefined;

    return (
        <motion.div variants={isOpen ? navItemVariants : undefined}>
            <div className={cn("group flex items-center gap-0.5", !isOpen && "justify-center")}>
                {targetPath ? (
                    <Link to={targetPath} className={itemClasses} title={title} onClick={onMobileClick} aria-current={isActive ? 'page' : undefined}>
                        {content}
                    </Link>
                ) : (
                    <button type="button" onClick={handleClick} className={itemClasses} title={title}>
                        {content}
                    </button>
                )}
                {showFavoriteToggle && isOpen && onToggleFavorite && (
                    <button
                        type="button"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onToggleFavorite();
                        }}
                        className={cn(
                            "btn-icon btn-icon-sm touch-manipulation transition-opacity",
                            itemIsFavorite
                                ? "opacity-100 text-yellow-400 hover:text-yellow-300"
                                : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                            isMobile && !itemIsFavorite && "opacity-60"
                        )}
                        title={itemIsFavorite ? "Remove from favourites" : "Add to favourites"}
                        aria-label={itemIsFavorite ? "Remove from favourites" : "Add to favourites"}
                    >
                        <Star className={cn("w-3.5 h-3.5", itemIsFavorite && "fill-current")} />
                    </button>
                )}
                {showDragHandle && isOpen && (
                    <span className="inline-flex items-center justify-center w-7 h-7 text-glass-500 cursor-grab active:cursor-grabbing" title="Drag to reorder">
                        <GripVertical className="w-3.5 h-3.5" />
                    </span>
                )}
            </div>
        </motion.div>
    );
  };
